/**
 * UpcomingFirmEvents
 *
 * Right-rail teaser card on the public `/uni/{id}` Overview tab.
 * Fetches the next 3 firm-hosted events pinned to this university and
 * links out to the full sector-filtered feed at `/deadlines?university={id}`.
 *
 * Silent-fail when there are no events yet — universities without any
 * pinned firm activity simply don't render this card.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, ExternalLink, Check, Loader2 } from "lucide-react";
import { useDeadlineImpression, trackDeadlineClick } from "@/hooks/useDeadlineImpression";


function EventRow({ r, isRsvped, pending, onToggleRsvp }) {
  const impressionRef = useDeadlineImpression(r.id, { source: "uni_teaser" });
  const d = daysOut(r.deadline_at);
  return (
    <li
      ref={impressionRef}
      className="pb-3 border-b border-[#4C1D95]/10 last:border-b-0 last:pb-0"
      data-testid={`uni-upcoming-event-${r.id}`}
    >
      <div className="flex items-start justify-between gap-2 text-[11px] font-mono text-stone-500">
        <span className="truncate font-medium text-stone-900">
          {r.firm_name || r.firm_slug}
        </span>
        <span className={d <= 3 ? "text-red-600 font-semibold shrink-0" : "shrink-0"}>
          {fmtShort(r.deadline_at)}
        </span>
      </div>
      {r.sponsored_paid_at && (
        <div className="mt-1">
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-1.5 py-0.5 uppercase tracking-wide"
            data-testid={`uni-upcoming-event-sponsored-${r.id}`}
          >
            Sponsored
          </span>
        </div>
      )}
      <div className="mt-0.5 text-[13px] font-medium text-stone-900 line-clamp-2">
        {r.title}
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="text-[10px] py-0 px-1.5">
          {KIND_LABEL[r.kind] || r.kind}
        </Badge>
        {r.city && (
          <span className="text-[10px] font-mono text-stone-500">
            {r.city}
          </span>
        )}
        <button
          type="button"
          onClick={() => onToggleRsvp(r.id)}
          disabled={pending}
          data-testid={`uni-upcoming-event-rsvp-${r.id}`}
          className={
            "ml-auto inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-underground-sc tracking-widest border transition " +
            (isRsvped
              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
              : "bg-white text-[#4C1D95] border-[#4C1D95] hover:bg-[#4C1D95]/10")
          }
        >
          {pending ? (
            <Loader2 className="h-2.5 w-2.5 animate-spin" />
          ) : isRsvped ? (
            <><Check className="h-2.5 w-2.5" /> Going</>
          ) : (
            "RSVP"
          )}
        </button>
        {r.apply_url && (
          <a
            href={r.apply_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackDeadlineClick(r.id, "uni_teaser")}
            className="inline-flex items-center gap-0.5 text-[10px] font-mono text-[#4C1D95] hover:underline"
            data-testid={`uni-upcoming-event-apply-${r.id}`}
          >
            Details <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    </li>
  );
}


const KIND_LABEL = {
  grad_scheme: "Grad scheme",
  internship: "Internship",
  spring_insight: "Spring insight",
  open_day: "Open day",
  hackathon: "Hackathon",
  scholarship: "Scholarship",
  drinks_canapes: "Drinks & canapés",
  meet_and_greet: "Meet & greet",
  presentation: "Presentation",
  panel_discussion: "Panel discussion",
  coffee_chat: "Coffee & conversation",
  competition: "Competition",
  workshop: "Workshop",
  summit: "Summit",
  trading_simulation: "Trading sim",
  careers_fair: "Careers fair",
  alumni_networking: "Alumni networking",
  case_study_night: "Case-study night",
  insight_day: "Insight day",
};


function fmtShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit", month: "short",
    });
  } catch { return iso; }
}


function daysOut(iso) {
  const then = new Date(iso);
  return Math.ceil((then - new Date()) / (1000 * 60 * 60 * 24));
}


export default function UpcomingFirmEvents({ universityId }) {
  const { user, openSignIn } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState(new Set()); // deadline_ids
  const [pending, setPending] = useState(null); // deadline_id being toggled

  useEffect(() => {
    if (!universityId) return;
    let cancel = false;
    (async () => {
      try {
        const [{ data }, mine] = await Promise.all([
          api.get(`/universities/${universityId}/upcoming-events`, {
            params: { limit: 3 },
          }),
          user
            ? api.get("/me/rsvps").catch(() => ({ data: { items: [] } }))
            : Promise.resolve({ data: { items: [] } }),
        ]);
        if (cancel) return;
        setRows(data?.items || []);
        setRsvps(new Set((mine.data?.items || []).map((r) => r.deadline_id)));
      } catch {
        if (!cancel) setRows([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [universityId, user?.user_id]);

  const toggleRsvp = async (deadline_id) => {
    if (!user) { openSignIn?.(); return; }
    setPending(deadline_id);
    try {
      if (rsvps.has(deadline_id)) {
        await api.delete(`/deadlines/${deadline_id}/rsvp`);
        setRsvps((s) => { const n = new Set(s); n.delete(deadline_id); return n; });
        toast.success("RSVP cancelled");
      } else {
        await api.post(`/deadlines/${deadline_id}/rsvp`);
        setRsvps((s) => new Set([...s, deadline_id]));
        toast.success("RSVP confirmed — the firm can see you're coming");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "RSVP failed");
    } finally {
      setPending(null);
    }
  };

  // Loading state: skeleton (matches the site convention)
  if (loading) return null;
  // Silent fail — don't render when empty
  if (!rows.length) return null;

  return (
    <Card
      className="p-4 border border-[#4C1D95]/25 bg-[#F5F0FF]/30"
      data-testid={`uni-upcoming-events-${universityId}`}
    >
      <div className="flex items-center gap-2 text-[#4C1D95]">
        <CalendarClock className="h-4 w-4" />
        <span className="text-[10px] font-underground-sc tracking-widest">
          Upcoming firm events
        </span>
      </div>

      <ul className="mt-3 space-y-3">
        {rows.map((r) => (
          <EventRow
            key={r.id}
            r={r}
            isRsvped={rsvps.has(r.id)}
            pending={pending === r.id}
            onToggleRsvp={toggleRsvp}
          />
        ))}
      </ul>

      <div className="mt-3 pt-2 border-t border-[#4C1D95]/10">
        <Link
          to={`/deadlines?university=${universityId}`}
          data-testid="uni-upcoming-events-see-all"
          className="text-[11px] font-underground-sc tracking-widest text-[#4C1D95] hover:underline"
        >
          See all firm events at this uni →
        </Link>
      </div>
    </Card>
  );
}

export { UpcomingFirmEvents };
